import type {
  CalendarResponse,
  MissingItem,
  CalendarView,
  CreateScheduledPostInput,
  PostingTarget,
  PostingTargetInput,
  ScheduledPost,
  UpdateScheduledPostInput,
} from '@qlk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, goiApi, type LoiApi } from '../../lib/api.js';

const GOC = '/api/v1/schedule';

export interface BoLocLich {
  view: CalendarView;
  anchor: string;
  channelId?: string;
}

export const KHOA = {
  lich: (loc: BoLocLich) => ['schedule', 'calendar', loc] as const,
  mucTieu: (channelId: string) => ['schedule', 'target', channelId] as const,
  theSanSang: ['schedule', 'ready-items'] as const,
};

/** Thẻ sản xuất đã qua Kiểm duyệt nhưng chưa có lịch. */
export interface TheSanSang {
  productionItemId: string;
  channelId: string;
  channelName: string;
  title: string;
  stage: string;
  missing: MissingItem[];
}

export function useTheSanSang() {
  return useQuery<TheSanSang[], LoiApi>({
    queryKey: KHOA.theSanSang,
    queryFn: () => api.get<TheSanSang[]>(`${GOC}/the-san-sang`),
  });
}

/**
 * Lịch đăng của một tuần hoặc một tháng.
 *
 * `bat = false` thì không gọi API — dùng cho người không có quyền
 * `schedule:view` (bảng Sản xuất đọc lịch để hiện ngày đăng trên thẻ).
 */
export function useLich(loc: BoLocLich, bat = true) {
  const sp = new URLSearchParams({ view: loc.view, anchor: loc.anchor });
  if (loc.channelId) sp.set('channelId', loc.channelId);

  return useQuery<CalendarResponse, LoiApi>({
    queryKey: KHOA.lich(loc),
    queryFn: () => api.get<CalendarResponse>(`${GOC}/lich?${sp.toString()}`),
    enabled: bat,
  });
}

export function useMucTieu(channelId: string | undefined) {
  return useQuery<PostingTarget, LoiApi>({
    queryKey: KHOA.mucTieu(channelId ?? ''),
    queryFn: () => api.get<PostingTarget>(`${GOC}/muc-tieu/${channelId ?? ''}`),
    enabled: Boolean(channelId),
  });
}

/* ------------------------------ Mutation ----------------------------- */

function lamMoiLich(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['schedule', 'calendar'] }),
    qc.invalidateQueries({ queryKey: KHOA.theSanSang }),
  ]);
}

export function useTaoPost() {
  const qc = useQueryClient();
  return useMutation<ScheduledPost, LoiApi, CreateScheduledPostInput>({
    mutationFn: (input) => api.post<ScheduledPost>(`${GOC}/posts`, input),
    onSuccess: () => lamMoiLich(qc),
  });
}

export function useCapNhatPost() {
  const qc = useQueryClient();
  return useMutation<ScheduledPost, LoiApi, { id: string } & UpdateScheduledPostInput>({
    mutationFn: ({ id, ...than }) =>
      goiApi<ScheduledPost>(`${GOC}/posts/${id}`, { method: 'PATCH', body: JSON.stringify(than) }),
    onSettled: () => lamMoiLich(qc),
  });
}

export function useXoaPost() {
  const qc = useQueryClient();
  return useMutation<void, LoiApi, string>({
    mutationFn: (id) => goiApi<void>(`${GOC}/posts/${id}`, { method: 'DELETE' }),
    onSuccess: () => lamMoiLich(qc),
  });
}

export function useLuuMucTieu(channelId: string) {
  const qc = useQueryClient();
  return useMutation<PostingTarget, LoiApi, PostingTargetInput>({
    mutationFn: (input) =>
      goiApi<PostingTarget>(`${GOC}/muc-tieu/${channelId}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: async (du) => {
      qc.setQueryData(KHOA.mucTieu(channelId), du);
      await lamMoiLich(qc);
    },
  });
}
